import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './lib/i18n';
import './styles/index.css';
import { initMonitoring } from './lib/monitoring';
import { routeTree } from './routeTree.gen';

// Before the first render, so an error during startup is still reported.
initMonitoring();

/**
 * The in-browser mock API (web-team-rules.md, "In-browser mock API"):
 * installed only when VITE_MOCK_API === 'true', via a dynamic import so a
 * production build never bundles src/mocks/** at all. Must resolve before
 * the router's first render issues any `apiRequest` call.
 */
async function bootstrap(): Promise<void> {
  if (import.meta.env['VITE_MOCK_API'] === 'true') {
    const { installMockApi } = await import('./mocks/install');
    installMockApi();
  }
  renderApp();
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function renderApp(): void {
  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root container #root is missing from index.html');
  }

  createRoot(container).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}

void bootstrap();
